import { PlusIcon } from "lucide-react";
import { type Control, type UseFieldArrayAppend, type UseFieldArrayRemove, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import type { SectionRegistrar } from "../../../../../hooks/useScrollSpy";
import type { ScrollSpySection } from "../../../../stipulations/stipulationCategoryFilters";
import { type ApplicantSsnType, MAX_INCOMES_PER_PERSON } from "../constants";
import type { IncomeInput, SignersInput } from "../Signers.schema";
import { makeEmptyIncome } from "../Signers.schema";
import { BankInformation } from "./BankInformation";
import { CustomerInformation } from "./CustomerInformation";
import { IncomeBlock } from "./IncomeBlock";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type PersonPath = "driver" | "secondSigner";

interface SignerColumnProperties {
  title: string;
  personPath: PersonPath;
  control: Control<SignersInput>;
  fields: Array<IncomeInput & { id: string }>;
  append: UseFieldArrayAppend<SignersInput, `${PersonPath}.incomes`>;
  remove: UseFieldArrayRemove;
  /** Deal / approval id, threaded down to each income block's employer-info flow. */
  approvalId?: string;
  /** Applicant SSN/ITIN status — filters the Income Type list and seeds new blocks. */
  applicantSsnType: ApplicantSsnType;
  /** Registers this column's Income/Bank/Residence sections with the Signers-level scroll-spy. */
  registerSection: SectionRegistrar<ScrollSpySection>;
}

// USD, always two decimals (e.g. $4,200.00). Built once at module scope.
const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formats the combined gross income for display, defaulting an absent value to $0.00.
 * @param {number | undefined} amount - The summed monthly gross for the signer.
 * @returns {string} A formatted USD string, e.g. "$4,200.00".
 */
function formatTotalGrossIncome(amount: number | undefined): string {
  return USD_FORMATTER.format(amount ?? 0);
}

// ---------------------------------------------------------------------------
// SignerColumn
// ---------------------------------------------------------------------------
export function SignerColumn({
  title,
  personPath,
  control,
  fields,
  append,
  remove,
  approvalId,
  applicantSsnType,
  registerSection,
}: SignerColumnProperties) {
  const { t } = useTranslation();
  const atCap = fields.length >= MAX_INCOMES_PER_PERSON;

  // Total Combined Gross Income — live sum of every income block's Gross Monthly
  // Pay for this signer; recomputes as blocks are added/removed/edited (and as
  // the calculator / Pay per Period updates each block's Gross Monthly Pay).
  const incomes = useWatch({ control, name: `${personPath}.incomes` }) as IncomeInput[] | undefined;
  const totalGrossMonthlyPay = (incomes ?? []).reduce((sum, income) => sum + (Number(income?.grossMonthlyPay) || 0), 0);

  function handleAddIncome() {
    if (!atCap) {
      append(makeEmptyIncome(applicantSsnType));
    }
  }

  return (
    <section aria-label={title} className="flex flex-col gap-3">
      {/* Person header */}
      <div className="border-border border-b pb-2">
        <h3 className="text-foreground text-sm font-semibold">{title}</h3>
      </div>

      {/* Income blocks */}
      <div ref={registerSection("Income")} className="flex flex-col gap-3">
        {fields.map((field, index) => (
          <IncomeBlock
            key={field.id}
            control={control}
            personPath={personPath}
            index={index}
            onRemove={index > 0 ? () => remove(index) : undefined}
            approvalId={approvalId}
            applicantSsnType={applicantSsnType}
          />
        ))}
      </div>

      {/* Total Combined Gross Income (left) + Add Income control (right) share a row */}
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-baseline gap-2 text-sm">
          <span className="text-foreground font-semibold">{formatTotalGrossIncome(totalGrossMonthlyPay)}</span>
          <span className="text-muted-foreground">{t("signers.totalCombinedGrossIncome")}</span>
        </p>
        {/* Add 2nd / 3rd Income — hidden at cap */}
        {!atCap && (
          <Button type="button" variant="link" size="sm" onClick={handleAddIncome} className="h-auto p-0">
            <PlusIcon className="size-4" aria-hidden="true" />
            {t("signers.addIncome", { position: fields.length === 1 ? "2nd" : "3rd" })}
          </Button>
        )}
      </div>

      {/* Bank Information — one subsection per person, below income list */}
      <BankInformation control={control} personPath={personPath} sectionRef={registerSection("Bank")} />

      {/* Customer Information — per-person collapsible section, below Bank Information */}
      <CustomerInformation
        control={control}
        personPath={personPath}
        residenceSectionRef={registerSection("Residence")}
      />
    </section>
  );
}
